import React, { useCallback, useMemo } from 'react';
import propTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { cn } from 'lib/utils';
import CarrierDisclaimer from 'tailwind-components/CarrierDisclaimer/CarrierDisclaimer';
import {
  DISCLAIMER_COLORS,
  DISCLAIMER_ICONS
} from 'helpers/factory/carrierFactory';
import UntappedAgentCard from './UntappedAgentCard';
import { listUntappedAgents } from './untappedAgentsHelpers';

function UntappedLoadingPlaceholder() {
  return (
    <div
      className={cn(
        'flex min-h-[160px] w-full items-center justify-center gap-2',
        'rounded-[10px] border border-dashed border-grey-lighter bg-white p-6 text-grey'
      )}
    >
      <CircularProgress size={20} thickness={5} />
      <span className='text-sm'>Loading untapped agents...</span>
    </div>
  );
}

export default function AgencyUntappedSection({
  agencies,
  untappedAgents,
  loading,
  onAgentSuggested
}) {
  const mainZohoId = useSelector(
    state => state.user.mainZohoId || state.user.zohoData.id || ''
  );

  const untappedAgentList = useMemo(
    () => listUntappedAgents(untappedAgents),
    [untappedAgents]
  );

  const totalAgents = untappedAgentList.length;

  const handleAgentSuggested = useCallback(
    ({ agentId, agencyId }) => {
      onAgentSuggested?.(agentId, agencyId);
    },
    [onAgentSuggested]
  );

  const disclaimer = useMemo(() => {
    if (totalAgents === 0) return null;
    const label = totalAgents === 1 ? 'agent' : 'agents';
    return {
      text: `<b>${totalAgents} ${label}</b> have no active appointments and no open appointment requests. Expand each agent to review suggested carriers, or use <b>Suggest to agent</b> to email recommendations they can act on from the Agent Portal.`,
      color: DISCLAIMER_COLORS.BLUE,
      icon: DISCLAIMER_ICONS.INFO
    };
  }, [totalAgents]);

  if (!agencies?.length) return null;

  if (loading) {
    return (
      <section className='mt-4 flex w-full min-w-0 flex-col gap-3'>
        <UntappedLoadingPlaceholder />
      </section>
    );
  }

  if (totalAgents === 0) {
    return (
      <section className='mt-4 flex w-full min-w-0 flex-col gap-3'>
        <div className='flex flex-1 flex-col items-center justify-start py-8 text-center'>
          <p className='m-0 text-base font-semibold text-primary-darkened'>
            No untapped agents
          </p>
          <p className='mt-2 m-0 max-w-md text-sm text-grey'>
            Every agent in this agency has an active appointment, an open
            appointment request, or both.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className='mt-4 flex w-full min-w-0 flex-col gap-3'>
      {disclaimer && <CarrierDisclaimer disclaimer={disclaimer} />}

      <div className='flex flex-col gap-3'>
        {untappedAgentList.map(agent => (
          <UntappedAgentCard
            key={`${agent.agencyId}::${agent.agentId}`}
            agencyId={agent.agencyId}
            agencyName={agent.agencyName}
            mainZohoId={mainZohoId}
            agent={agent}
            availableCarriers={agent.availableCarriers}
            onSuggest={handleAgentSuggested}
          />
        ))}
      </div>
    </section>
  );
}

AgencyUntappedSection.propTypes = {
  agencies: propTypes.arrayOf(
    propTypes.shape({
      agencyId: propTypes.string.isRequired,
      agencyName: propTypes.string.isRequired
    })
  ),
  untappedAgents: propTypes.object,
  loading: propTypes.bool,
  onAgentSuggested: propTypes.func
};
